import { useEffect, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { isInTeams } from "./platform";
import i18n from "./language";

export type ThemeString = "light" | "dark" | "contrast";

/**
 * Normalize Teams explicit themes (exclude `default`)
 */
function normalizeTheme(theme?: string): ThemeString {
  switch (theme) {
    case "dark":
    case "contrast":
      return theme;
    default:
      return "light";
  }
}

/**
 * Get current OS theme
 */
function getSystemTheme(): ThemeString {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function useTeamsContext() {
  const [inTeams, setInTeams] = useState(false);
  const [themeString, setThemeString] = useState<ThemeString>("light");
  const [teamsTheme, setTeamsTheme] = useState<string | undefined>();
  const [language, setLanguage] = useState<string>("en");

  /**
   * Initial detection (Teams / Browser)
   */
  useEffect(() => {
    (async () => {
      const detected = await isInTeams();
      setInTeams(detected);

      if (detected) {
        const context = await microsoftTeams.app.getContext();

        const theme = context.app.theme;
        console.log("Teams detected Theme: ", theme)
        setTeamsTheme(theme);

        // --- Theme handling ---
        const normalized = normalizeTheme(theme === 'default' ? 'light' : theme);
        setThemeString(normalized);
        localStorage.setItem("Theme", normalized);

        // --- Language handling ---
        const locale = context.app.locale;
        const normalizedLocale = locale?.split("-")[0] ?? "en";
        setLanguage(normalizedLocale);
        i18n.changeLanguage(normalizedLocale);

        // --- Teams theme change listener ---
        microsoftTeams.app.registerOnThemeChangeHandler((newTheme) => {
          console.log("Teams theme changed:", newTheme);
          setTeamsTheme(newTheme);

          if (newTheme === "default") {
            const systemTheme = getSystemTheme();
            setThemeString(systemTheme);
            localStorage.setItem("Theme", systemTheme);
          } else {
            const normalized = normalizeTheme(newTheme);
            setThemeString(normalized);
            localStorage.setItem("Theme", normalized);
          }
        });
      } else {
        // -------- Standalone browser --------

        // Language
        const locale = navigator.language;
        const normalizedLocale = locale?.split("-")[0] ?? "en";
        setLanguage(normalizedLocale);
        i18n.changeLanguage(normalizedLocale);

        // Theme
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

        const applyTheme = () => {
          const systemTheme = mediaQuery.matches ? "dark" : "light";
          setThemeString(systemTheme);
          localStorage.setItem("Theme", systemTheme);
        };

        applyTheme();
        mediaQuery.addEventListener("change", applyTheme);

        return () => mediaQuery.removeEventListener("change", applyTheme);
      }
    })();
  }, []);

  /**
   * Listen to OS theme changes
   * ONLY when Teams theme = `default`
   */
  useEffect(() => {
    if (!inTeams || teamsTheme !== "default") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      const systemTheme = mediaQuery.matches ? "dark" : "light";
      setThemeString(systemTheme);
      localStorage.setItem("Theme", systemTheme);
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [inTeams, teamsTheme]);

  return { inTeams, themeString, language };
}
